import { useEffect, useState, useCallback, useRef } from "react";
import Card from "../components/Card";
import axios from "axios";
import { useAuth } from "../context/authContext";
import ConfirmationModal from "../components/ConfirmationModal";
import Modal from "../components/Modal";
import Filter from "../components/Filter";
import TimeshareMap from "../components/timeshareMap";
import { useNavigate } from "react-router-dom";
import ImageCarousel from "../components/ImageCarousel";
import lake from "../assets/lake.jpg";
import cabin from "../assets/cabin.jpg";
import SightsCarousel from "../components/sightCarousel";

const Home = () => {
  const [timeShares, setTimeShares] = useState([]);
  const [filteredTimeShares, setFilteredTimeShares] = useState([]);
  const [filters, setFilters] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [message, setMessage] = useState("");
  const [message2, setMessage2] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [selectedTimeshare, setSelectedTimeshare] = useState(null);
  const [expandedLocation, setExpandedLocation] = useState(null);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [availableWeeks, setAvailableWeeks] = useState([]);
  const [availableBedCounts, setAvailableBedCounts] = useState([]);
  const [availableSaunaOptions, setAvailableSaunaOptions] = useState([]);
  const [availableBalconyOptions, setAvailableBalconyOptions] = useState([]);
  const [discountCode, setDiscountCode] = useState("");
  const [discountValue, setDiscountValue] = useState(0);
  const [discountErrorMessage, setDiscountErrorMessage] = useState("");
  const [isBooking, setIsBooking] = useState(false);
  const [modalConfirmCallback, setModalConfirmCallback] = useState(null);
  const [discountApplied, setDiscountApplied] = useState(false);
  const [canBook, setCanBook] = useState(false);
  const [showDiscountInput, setShowDiscountInput] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL;
  const { user, updateUserCredits, loading } = useAuth();

  const navigate = useNavigate();
  const locationRef = useRef(null);

  useEffect(() => {
    console.log("user", user);
    if (!loading) {
      if (user?.membership?.active === 0) {
        navigate("/tervetuloa");
      }
    }
  }, [user, navigate, loading]);

  useEffect(() => {
    if (!user) return;

    const { deposits = 0, bookings = 0 } = user;

    if (bookings < deposits) {
      // user still has deposit "credit" for a booking
      setCanBook(true);
    } else {
      // user has used up all deposits for bookings
      setCanBook(false);
    }
  }, [user?.deposits, user?.bookings]);

  // Fetch timeshares once when the component mounts
  useEffect(() => {
    const fetchTimeShares = async () => {
      try {
        const response = await fetch(`${apiUrl}/timeshares/listed`, {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await response.json();
        console.log("data", data);

        setTimeShares(Array.isArray(data) ? data : []);
        updateUserCredits();
      } catch (error) {
        console.error("Error fetching timeShares:", error);
      }
    };

    fetchTimeShares();
  }, [apiUrl, updateUserCredits]);

  useEffect(() => {
    const handleClickInside = (event) => {
      if (locationRef.current && locationRef.current.contains(event.target)) {
        const clickedLocation = event.target.closest("button");
        if (
          !clickedLocation ||
          !Object.keys(locations).includes(
            clickedLocation.innerText.toLowerCase()
          )
        ) {
          setExpandedLocation(null); // Collapse the expanded location if clicked outside a location button
        }
      }
    };

    if (locationRef.current) {
      // Add the event listener if the ref is not null
      locationRef.current.addEventListener("mousedown", handleClickInside);
    }

    return () => {
      // Cleanup the event listener when the component unmounts or ref changes
      if (locationRef.current) {
        locationRef.current.removeEventListener("mousedown", handleClickInside);
      }
    };
  }, [locations]);

  const checkAvailability = useCallback(
    (filterKey, filterValue) => {
      switch (filterKey) {
        case "location":
          return timeShares.some((ts) => ts.location === filterValue);
        case "week":
          return timeShares.some((ts) => {
            const startDate = new Date(ts.start_date);
            return getISOWeek(startDate) === parseInt(filterValue);
          });
        case "bedCount":
          return timeShares.some(
            (ts) => ts.sleeping_places >= parseInt(filterValue)
          );
        case "sauna":
          return timeShares.some((ts) => ts.sauna === 1); // Use 1 for sauna filter
        case "balcony":
          return timeShares.some((ts) => ts.balcony_terrace === 1); // Use 1 for balcony filter
        default:
          return false;
      }
    },
    [timeShares]
  );

  // applyFilters is now safely guarded with useCallback
  const applyFilters = useCallback(() => {
    let filtered = [...timeShares];

    // Filter by location if selected
    if (filters.location) {
      filtered = filtered.filter((ts) => ts.location === filters.location);
    }

    // Calculate available options based on filtered timeshares
    const availableWeeks = new Set();
    const availableBedCounts = new Set();
    const availableSaunaOptions = new Set();
    const availableBalconyOptions = new Set();

    filtered.forEach((ts) => {
      const endDate = new Date(ts.end_date);
      availableWeeks.add(getISOWeek(endDate));
      availableBedCounts.add(ts.sleeping_places);
      if (ts.sauna) {
        availableSaunaOptions.add(true);
      }
      if (ts.balcony_terrace) {
        availableBalconyOptions.add(true);
      }
    });

    setAvailableWeeks(Array.from(availableWeeks));
    setAvailableBedCounts(Array.from(availableBedCounts).sort((a, b) => a - b));
    setAvailableSaunaOptions(Array.from(availableSaunaOptions));
    setAvailableBalconyOptions(Array.from(availableBalconyOptions));

    // Apply filters to get filtered timeshares
    if (filters.week) {
      filtered = filtered.filter((ts) => {
        const endDate = new Date(ts.end_date);
        return getISOWeek(endDate) === parseInt(filters.week);
      });
    }

    if (filters.bedCount) {
      filtered = filtered.filter(
        (ts) => ts.sleeping_places >= parseInt(filters.bedCount)
      );
    }

    if (filters.qualities === "sauna") {
      filtered = filtered.filter((ts) => ts.sauna === 1);
    }

    if (filters.qualities === "balcony") {
      filtered = filtered.filter((ts) => ts.balcony_terrace === 1);
    }

    setFilteredTimeShares(filtered);
  }, [filters, timeShares]);

  // Only apply filters when either timeShares or filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const onFilterChange = (newFilters) => {
    setFilters((prevFilters) => {
      // Only update filters if they actually change to prevent re-renders
      if (JSON.stringify(newFilters) !== JSON.stringify(prevFilters)) {
        return newFilters;
      }
      return prevFilters;
    });
  };

  const onBookingClick = (timeshare) => {
    if (user.credits >= timeshare.credits && !checkOffer(timeshare)) {
      // User has enough credits, proceed with standard payment
      initiateStandardPayment(timeshare);
    } else if (checkOffer(timeshare)) {
      // Offer available, initiate offer payment
      initiateOfferPayment(timeshare);
    } else {
      // Not enough credits, initiate payment with missing credits

      initiateInsufficientCreditsPayment(timeshare);
    }
  };

  const handleBookingClick = (timeshare, adjustedCredits) => {
    setSelectedTimeshare({ ...timeshare, adjustedCredits });

    const handleConfirm = async (discountCode) => {
      try {
        if (discountCode) {
          await validateDiscountCode(discountCode, timeshare, adjustedCredits);
        } else {
          setErrorMessage("");
          onBookingClick(timeshare);
        }
      } catch (error) {
        console.log("error", error);
        setDiscountErrorMessage("Virhe alennuskoodin tarkistuksessa.");
      }
    };

    const isOffer = checkOffer(timeshare);

    // Offers: no discount field
    if (isOffer) {
      setMessage("Haluatko varmasti varata tämän viikon 99,00€?");
      setIsBooking(true);
      setShowDiscountInput(true); // ✅ hide for offers
      setShowModal(true);
      setModalConfirmCallback(() => handleConfirm);
      return;
    }

    // Non-offers: require credits > 0 to proceed, but allow discount codes
    if (user.credits <= 0) {
      setMessage(
        "Lompakkosi on tyhjä. Tee uusi talletus voidaksesi varata loman."
      );
      setIsBooking(false);
      setShowDiscountInput(false); // info-only
      setShowModal(true);
      return;
    }

    // Normal non-offer flow
    if (user.credits >= adjustedCredits) {
      setMessage(
        `Haluatko varmasti varata tämän viikon ${adjustedCredits} lomakrediitillä + 129,00€ vaihtomaksu?`
      );
    } else {
      const missingCredits = adjustedCredits - user.credits;
      const additionalCost = missingCredits * 1;
      setMessage(
        `Sinulla ei ole tarpeeksi lomakrediittejä. Voit silti varata viikon maksamalla puuttuvat ${missingCredits} krediittiä (${additionalCost}€) + 129,00€ vaihtomaksu.`
      );
    }

    setIsBooking(true);
    setShowDiscountInput(true); // ✅ show for non-offers
    setShowModal(true);
    setModalConfirmCallback(() => handleConfirm);
  };

  const checkOffer = (timeshare) => {
    const currentDate = new Date();
    const timeshareStartDate = new Date(timeshare.start_date);
    const thirtyDaysFromNow = new Date();
    thirtyDaysFromNow.setDate(currentDate.getDate() + 30);

    return timeshareStartDate <= thirtyDaysFromNow;
  };

  const handleLocationClick = (location) => {
    setExpandedLocation(expandedLocation === location ? null : location);
  };

  const getISOWeek = (date) => {
    const tempDate = new Date(date);
    tempDate.setHours(0, 0, 0, 0);
    tempDate.setDate(tempDate.getDate() + 3 - ((tempDate.getDay() + 6) % 7));
    const week1 = new Date(tempDate.getFullYear(), 0, 4);
    return (
      1 +
      Math.round(
        ((tempDate - week1) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7
      )
    );
  };

  const calculateAdjustedCredits = (timeshare) => {
    const currentDate = new Date();
    const timeshareStartDate = new Date(timeshare.start_date);

    const fourWeeksFromNow = new Date();
    fourWeeksFromNow.setDate(currentDate.getDate() + 28);

    if (timeshareStartDate <= fourWeeksFromNow) {
      return Math.floor(timeshare.credits * 0.5);
    }
    return timeshare.credits;
  };

  const initiateStandardPayment = async (timeshare) => {
    try {
      let paymentAmount = 12900; // €129.90 in cents (default)

      // If a discount is applied, calculate the discounted price
      if (discountApplied && discountCode) {
        const discountResponse = await axios.post(
          `${apiUrl}/auth/validate-discount`,
          {
            code: discountCode,
            user_id: user.id,
            category: "Vaihtomaksut", // Explicitly validate for exchange fees
          }
        );

        const discount = discountResponse.data;
        if (discount.success) {
          const discountPercentage = Number(discount.discount) || 0;
          const discountAmountInCents = Math.floor(
            (paymentAmount * discountPercentage) / 100
          );
          paymentAmount -= discountAmountInCents;
        }
      }

      paymentAmount = Math.max(0, paymentAmount); // Ensure it's not negative

      // ✅ Skip Paytrail when nothing to charge
      if (paymentAmount === 0) {
        return completeBookingWithoutPayment(
          timeshare,
          timeshare.adjustedCredits
        );
      }

      const paymentData = {
        email: user.email,
        amount: paymentAmount,
        userId: user.id,
        credits: timeshare.adjustedCredits,
        reference: String(timeshare.unit_id),
        order: [
          {
            unitPrice: paymentAmount,
            units: 1,
            productCode: "3", // For standard booking
          },
        ],
      };

      console.log("paymentData", paymentData);

      // Send the payment initiation request
      const response = await axios.post(
        `${apiUrl}/payments/create-payment`,
        paymentData
      );
      const result = response.data;

      if (result.href) {
        // Redirect to Paytrail or another payment processor
        window.location.href = result.href;
      } else {
        console.log("Payment creation failed:", result);
      }
    } catch (error) {
      console.error("Error initiating standard payment:", error);
      setErrorMessage("Virhe maksuprosessin aloittamisessa. Yritä uudelleen.");
      setShowErrorModal(true);
    }
  };

  const initiateDiscountedPayment = async (
    timeshare,
    adjustedCredits,
    discountedAmountInCents
  ) => {
    const missingCredits = adjustedCredits - user.credits; // Calculate missing credits
    const missingCreditsCost = missingCredits > 0 ? missingCredits * 100 : 0; // €1 per credit in cents
    const totalAmount = discountedAmountInCents + missingCreditsCost; // Add the missing credits cost to the discounted exchange fee

    if (totalAmount === 0) {
      return completeBookingWithoutPayment(timeshare, adjustedCredits);
    }

    const paymentData = {
      email: user.email,
      amount: totalAmount, // Total payment amount (exchange fee + missing credits)
      userId: user.id,
      credits: adjustedCredits,
      reference: String(timeshare.unit_id),
      order: [
        {
          unitPrice: totalAmount,
          units: 1,
          productCode: "3", // Adjust product code as needed
        },
      ],
    };

    console.log("paymentData", paymentData);

    try {
      const response = await axios.post(
        `${apiUrl}/payments/create-payment`,
        paymentData
      );
      const result = response.data;

      if (result.href) {
        // Redirect to Paytrail or another payment processor
        window.location.href = result.href;
      } else {
        console.error("Payment creation failed:", result);
        setErrorMessage("Maksuprosessin luonti epäonnistui.");
        setShowErrorModal(true);
      }
    } catch (error) {
      console.error("Error initiating discounted payment:", error);
      setErrorMessage("Virhe maksuprosessin aloittamisessa.");
      setShowErrorModal(true);
    }
  };

  const initiateOfferPayment = async (timeshare) => {
    if (!checkOffer(timeshare)) {
      return;
    }

    try {
      const paymentData = {
        email: user.email,
        amount: 9900, // Amount for the offer
        userId: user.id,
        reference: String(timeshare.unit_id),
        order: [
          {
            unitPrice: 9900,
            units: 1,
            productCode: "2",
          },
        ],
      };

      const response = await axios.post(
        `${apiUrl}/payments/create-payment`,
        paymentData
      );
      const result = response.data;

      if (result.href) {
        window.location.href = result.href; // Redirect to Paytrail
      } else {
        console.log("Payment creation failed:", result);
      }
    } catch (error) {
      console.error("Error initiating payment:", error);
      setErrorMessage("Virhe maksuprosessin aloittamisessa. Yritä uudelleen.");
      setShowErrorModal(true);
    }
  };

  const initiateInsufficientCreditsPayment = async (timeshare) => {
    const missingCredits = timeshare.credits - user.credits;
    const missingCreditsCost = missingCredits * 100; // €1 per credit in cents
    let paymentAmount = 12900 + missingCreditsCost; // €129.90 exchange fee + missing credits cost

    try {
      // If a discount is applied, calculate the discounted price
      if (discountApplied && discountCode) {
        const discountResponse = await axios.post(
          `${apiUrl}/auth/validate-discount`,
          {
            code: discountCode,
            user_id: user.id,
            category: "Vaihtomaksut", // Explicitly validate for exchange fees
          }
        );

        const discount = discountResponse.data;
        if (discount.success) {
          const discountPercentage = Number(discount.discount) || 0;
          const discountAmountInCents = Math.floor(
            (12900 * discountPercentage) / 100 // Only apply the discount to the exchange fee
          );
          paymentAmount -= discountAmountInCents;
        }
      }

      paymentAmount = Math.max(0, paymentAmount); // Ensure it's not negative

      if (paymentAmount === 0) {
        return completeBookingWithoutPayment(
          timeshare,
          timeshare.adjustedCredits
        );
      }

      const paymentData = {
        email: user.email,
        amount: paymentAmount,
        userId: user.id,
        credits: timeshare.adjustedCredits,
        reference: String(timeshare.unit_id),
        order: [
          {
            unitPrice: paymentAmount, // Total payment amount
            units: 1,
            productCode: "4",
          },
        ],
      };

      const response = await axios.post(
        `${apiUrl}/payments/create-payment`,
        paymentData
      );
      const result = response.data;

      if (result.href) {
        // Redirect to the payment page
        window.location.href = result.href;
      } else {
        console.log("Payment creation failed:", result);
      }
    } catch (error) {
      console.error("Error initiating payment with missing credits:", error);
      setErrorMessage("Virhe maksuprosessin aloittamisessa. Yritä uudelleen.");
      setShowErrorModal(true);
    }
  };

  const validateDiscountCode = async (
    discountCode,
    timeshare,
    adjustedCredits
  ) => {
    console.log("Validating discount code");

    try {
      // Call the backend API to validate the discount code
      const response = await axios.post(`${apiUrl}/auth/validate-discount`, {
        code: discountCode,
        user_id: user.id, // Send the user ID for validation
        category: "Vaihtomaksut", // Specify the category for exchange fee discounts
      });

      console.log("Response from validation:", response.data);

      const { success, error, discount, code } = response.data;

      if (!success) {
        // Handle specific backend error codes
        switch (code) {
          case "INVALID_CODE":
            setDiscountErrorMessage("Alennuskoodi on virheellinen."); // Invalid code
            break;
          case "NOT_YOUR_CODE":
            setDiscountErrorMessage("Alennuskoodi ei ole sinulle."); // Not assigned to this user
            break;
          case "EXPIRED_CODE":
            setDiscountErrorMessage("Alennuskoodi on vanhentunut."); // Expired code
            break;
          case "CODE_ALREADY_USED":
            setDiscountErrorMessage("Alennuskoodi on jo käytetty."); // Already used
            break;
          default:
            setDiscountErrorMessage(
              "Tuntematon virhe alennuskoodin tarkistuksessa."
            );
        }
        return; // Stop further execution if the discount code validation fails
      }

      // Calculate the discounted amount
      const baseAmountInCents = 12900; // Base price in cents for exchange fees
      const discountPercentage = Number(discount) || 0; // Get discount percentage

      // Calculate the discount amount in cents
      const discountAmountInCents = Math.floor(
        (baseAmountInCents * discountPercentage) / 100
      );

      // Apply the discount to the base amount
      const discountedAmountInCents = baseAmountInCents - discountAmountInCents;

      console.log("discountPercentage", discountPercentage); // Log the discount percentage
      console.log("discountAmountInCents", discountAmountInCents); // Log the calculated discount amount
      console.log("discountedAmountInCents", discountedAmountInCents); // Log the final discounted amount

      // Proceed with discounted payment
      await initiateDiscountedPayment(
        timeshare,
        adjustedCredits,
        discountedAmountInCents
      );
    } catch (error) {
      console.error("Error validating discount code:", error);

      // Handle error from backend response
      if (error.response && error.response.data) {
        const { code } = error.response.data;
        switch (code) {
          case "INVALID_CODE":
            setDiscountErrorMessage("Alennuskoodi on virheellinen.");
            break;
          case "NOT_YOUR_CODE":
            setDiscountErrorMessage("Alennuskoodi ei ole sinulle.");
            break;
          case "EXPIRED_CODE":
            setDiscountErrorMessage("Alennuskoodi on vanhentunut.");
            break;
          case "CODE_ALREADY_USED":
            setDiscountErrorMessage("Alennuskoodi on jo käytetty.");
            break;
          default:
            setDiscountErrorMessage(
              "Tuntematon virhe alennuskoodin tarkistuksessa."
            );
        }
      } else {
        // Fallback for unexpected errors
        setDiscountErrorMessage(
          "Virhe alennuskoodin tarkistuksessa. Yritä uudelleen."
        );
      }
    }
  };

  const completeBookingWithoutPayment = async (timeshare, adjustedCredits) => {
    try {
      const payload = {
        userId: user.id,
        unitId: timeshare.unit_id,
        credits: adjustedCredits ?? timeshare.adjustedCredits,
        // include any other fields you need, e.g. discountCode if relevant
      };

      const res = await axios.post(
        `${apiUrl}/timeshares/complete-free`,
        payload,
        {
          withCredentials: true,
        }
      );

      if (res.status === 200) {
        setShowModal(false);
        setIsBooking(false);
        setMessage2("Varaus tehty onnistuneesti!");
        setShowMessageModal(true);
        setShowDiscountInput(false);
      } else {
        throw new Error("Booking completion failed.");
      }
    } catch (err) {
      console.error("Error completing free booking:", err);
      setErrorMessage("Varausta ei voitu viimeistellä.");
      setShowErrorModal(true);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-transparent flex flex-col justify-start p-4">
        <section className="mb-5">
          <SightsCarousel />
        </section>

        <section className="rounded-2xl relative overflow-hidden bg-gradient-to-br from-slate-800 via-teal-900 to-teal-600">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-center gap-10 lg:grid-cols-2 py-16 lg:py-24">
              {/* Left: text */}
              <div className="text-left">
                <h1 className="text-white font-semibold leading-tight text-4xl sm:text-5xl lg:text-6xl">
                  Vaihda lomaosakeviikkosi
                  <br />
                  <span className="text-teal-300">
                    suomalaisiin lomakohteisiin
                  </span>
                </h1>

                <p className="mt-6 max-w-xl text-teal-100/90 text-base sm:text-lg">
                  Kotimainen vaihtopalvelu, joka yhdistää lomaosakkeiden
                  omistajat ympäri Suomea. Yksinkertainen, edullinen ja
                  luotettava. Hinnat: 129€/vko ja äkkilähdöt 99€/vko
                </p>

                {/* Buttons were in the screenshot, but you said to ignore them */}
              </div>

              {/* Right: image card */}
              <div className="relative">
                <div className="overflow-hidden rounded-3xl shadow-2xl ring-1 ring-white/10 bg-teal-900/20">
                  <img
                    src={cabin}
                    alt="Lomakohde"
                    className="h-[340px] w-full object-cover md:h-[420px]"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* optional subtle vignette for depth */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/10" />
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900">
              Miksi valita Vaihtolomat.fi?
            </h2>
            <p className="mt-3 text-slate-600">
              Olemme luoneet yksinkertaisen ja luotettavan tavan vaihtaa
              lomaosakeviikkosi muihin suomalaisiin lomakohteisiin.
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {/* Card 1 */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-left shadow-sm hover:shadow-md transition-shadow">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-600">
                  {/* location icon */}
                  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                    <path
                      d="M12 2a7 7 0 0 0-7 7c0 5.25 7 13 7 13s7-7.75 7-13a7 7 0 0 0-7-7z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <circle
                      cx="12"
                      cy="9"
                      r="2.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Kotimainen verkosto
                </h3>
                <p className="mt-2 text-slate-600">
                  Laaja valikoima suomalaisia lomakohteita Lapista
                  Saaristomerelle. Tutustu omaan maahasi uudella tavalla.
                </p>
              </div>

              {/* Card 2 */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-left shadow-sm hover:shadow-md transition-shadow">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-600">
                  {/* arrows icon */}
                  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                    <path
                      d="M3 7h13l-2-2M21 17H8l2 2"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 7l-4 4 4 4M17 17l4-4-4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Yksinkertainen vaihto
                </h3>
                <p className="mt-2 text-slate-600">
                  Helppo ja nopea prosessi. Talleta viikkosi ja varaa muiden
                  tallettamia viikkoja hintaan 129€/vko.
                </p>
              </div>

              {/* Card 3 */}
              <div className="rounded-2xl border border-slate-200 bg-white p-8 text-left shadow-sm hover:shadow-md transition-shadow">
                <div className="mb-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 text-teal-600">
                  {/* star icon */}
                  <svg viewBox="0 0 24 24" fill="none" className="h-6 w-6">
                    <path
                      d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27z"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-slate-900">
                  Luotettava palvelu
                </h3>
                <p className="mt-2 text-slate-600">
                  Suomalainen yritys, joka ymmärtää paikallisia tarpeita.
                  Asiakaspalvelu suomeksi.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="mx-auto max-w-6xl px-4 text-center">
            <h2 className="text-3xl sm:text-4xl font-semibold text-slate-900">
              Näin vaihto toimii
            </h2>
            <p className="mt-3 text-slate-600">
              Yksinkertainen kolmen vaiheen prosessi vie sinut unelmalomallesi
            </p>

            <div className="mt-12 grid gap-8 sm:grid-cols-3">
              {/* Step 1 */}
              <div>
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-teal-600 text-white text-2xl font-bold">
                  1
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  Rekisteröidy
                </h3>
                <p className="mt-2 text-slate-600 text-sm">
                  Luo tili ja lisää oma lomaosakkeesi järjestelmään. Sinun
                  tarvitsee vain tietää omistamasi kohde, viikon alkamispäivä
                  sekä kuinka monta henkilöä huoneistoon voi majoittua.
                </p>
              </div>

              {/* Step 2 */}
              <div>
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-teal-600 text-white text-2xl font-bold">
                  2
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  Etsi ja valitse
                </h3>
                <p className="mt-2 text-slate-600 text-sm">
                  Selaa saatavilla olevia kohteita ja löydä täydellinen
                  vaihtokohteesi haluamallesi ajankohdalle.
                </p>
              </div>

              {/* Step 3 */}
              <div>
                <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-teal-600 text-white text-2xl font-bold">
                  3
                </div>
                <h3 className="mt-4 text-lg font-semibold text-slate-900">
                  Nauti lomastasi
                </h3>
                <p className="mt-2 text-slate-600 text-sm">
                  Vahvista vaihto ja nauti upeasta lomastasi uudessa kohteessa.
                  Yhtä helppoa kuin se kuulostaa!
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="bg-slate-100">
      {/* Filter for logged-in users (only if has any deposits) */}
      {user && (user.deposits || 0) > 0 && (
        <div className="flex justify-center">
          <Filter
            onFilterChange={onFilterChange}
            checkAvailability={checkAvailability}
            availableWeeks={availableWeeks}
            availableBedCounts={availableBedCounts}
            availableSaunaOptions={availableSaunaOptions}
            availableBalconyOptions={availableBalconyOptions}
          />
        </div>
      )}

      <Modal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={errorMessage}
      />

      <Modal
        isVisible={showMessageModal}
        onClose={() => setShowMessageModal(false)}
        children={message2}
      />

      <div className="flex items-center justify-center">
        {user ? (
          user.membership?.active === 1 ? (
            /* 🔒 Gate browsing behind deposits */
            (user.deposits || 0) <= 0 ? (
              <div className="bg-white flex flex-col justify-center rounded-lg shadow-lg p-4 m-4 bg-opacity-50 max-w-xl">
                <p className="text-xl text-center m-4">
                  Talleta viikko päästäksesi selaamaan vapaita lomia.
                </p>
                <button
                  onClick={() => navigate("/profiili")}
                  className="flex w-3/4 lg:w-1/2 justify-center mx-auto mt-4 rounded-3xl bg-teal-600 px-5 py-3 text-md lg:text-xl font-roboto leading-6 text-white shadow-sm hover:bg-teal-700 focus-visible:outline focus:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2"
                >
                  Siirry profiiliin
                </button>
              </div>
            ) : (
              /* ✅ User has deposits: show listings */
              <>
                {filteredTimeShares.length === 0 ? (
                  <p>Ei hakuehtoja vastaavia lomia saatavilla.</p>
                ) : (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {filteredTimeShares.map((timeshare) => (
                        <Card
                          key={timeshare.unit_id}
                          timeshare={timeshare}
                          adjustedCredits={calculateAdjustedCredits(timeshare)}
                          buttonText="Varaa nyt"
                          onClick={() =>
                            handleBookingClick(
                              timeshare,
                              calculateAdjustedCredits(timeshare)
                            )
                          }
                          offer={checkOffer(timeshare)}
                        />
                      ))}
                    </div>
                    {showModal && selectedTimeshare && (
                      <ConfirmationModal
                        prompt={message}
                        onConfirm={modalConfirmCallback}
                        onCancel={() => setShowModal(false)}
                        showDiscountInput={showDiscountInput} // ✅ NEW
                        discountErrorMessage={discountErrorMessage} // ✅ shows validation errors
                      />
                    )}
                  </>
                )}
              </>
            )
          ) : (
            /* No active membership */
            <div className="bg-white flex flex-col justify-center rounded-lg shadow-lg p-4 m-4 bg-opacity-50">
              <p className="text-xl text-center m-4">
                Sinulla ei ole aktiivista jäsenyyttä.
              </p>
              <button
                onClick={() => navigate("/tervetuloa")}
                className="flex w-3/4 lg:w-1/2 justify-center mx-auto rounded-3xl bg-teal-600 px-5 py-3 text-md lg:text-xl font-roboto leading-6 text-white shadow-sm hover:bg-teal-700 focus-visible:outline focus:bg-teal-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange"
              >
                Osta jäsenyys
              </button>
            </div>
          )
        ) : (
          <div className="flex flex-col justify-center w-9/12"></div>
        )}
      </div>
    </div>
  );
};

export default Home;

const locations = {
  airisto: ["Airiston Fregatti", "Airiston Kuunari", "Airiston Kutteri"],
  ellivuori: ["Ellin Loisto I"],
  kivijärvi: ["Hannunkiven Lomakylä"],
  himos: [
    "Himoksen Aurinkopaikka",
    "Himoksen Tähti I",
    "Villas Himos I",
    "Villas Himos II",
    "Villas Himos III",
  ],
  hoilola: ["Onnenvirta II", "Onnenvirta III"],
  ikaalinen: ["Ikaalisten Mäntypiha"],
  imatra: ["Imatra Spa Viikko Oy"],
  kalajoki: [
    "Särkkäin lomaparatiisi I",
    "Särkkäin lomaparatiisi II",
    "Rantabeach",
    "Kalajoen Keidas",
  ],
  vuokatti: [
    "Katinkullan Golfharju",
    "Katinkullan Hiekkaniemi",
    "Katinkullan Kiinteistöt",
    "Katinkultaniemi",
    "Katinkultaranta",
    "Katinkullan Rantahovi",
    "Katinkulta Residence",
    "Katinkullan Golfpuisto",
    "Katinkulta Spa Lodge 1",
    "Katinkulta Spa Lodge 2",
    "Villas Katinkulta Spa 1",
    "Villas Katinkulta Spa 1 Lodge",
    "Villas Katinkulta Spa 2",
    "Villas Katinkulta Golf Park",
    "Vuokatti Country Club",
    "Vuokatin Kulta-Katti",
    "Vuokatin Lepokatti",
  ],
  koli: ["Kolin Kukkula"],
  kihniö: [
    "Pyhäniemi II",
    "Pyhäniemi III",
    "Pyhäniemi IV",
    "Pyhäniemi V",
    "Pyhäniemi VI",
    "Pyhäniemi VII",
    "Pyhäniemi VIII",
  ],
  kuortane: ["Kuortaneen Liikuntahotelli"],
  kuusamo: [
    "Kuusamon Lampitropiikki",
    "Kuusamon Tähti 1",
    "Kuusamon Lomaparatiisi",
    "Kuusamon Lomatropiikki",
    "Kuusamon Rantatropiikki",
    "Kuusamon Rantatropiikki 2",
    "Petäjälampi 6 Lodge",
    "Petäjälammenranta 7 Lodge",
  ],
  laukaa: ["Pitkäniemi III"],
  levi: [
    "Levi-Rakkavaara Club 1",
    "Rakkavaara Club Int. Ltd",
    "Abgott",
    "Aruudenia",
  ],
  naantali: ["Naantalin kylpyläranta", "Sunborn Vacation Club 1"],
  punkaharju: ["Hiekkaharju 1", "Hiekkaharju 2"],
  pyhä: ["Onninpyhä", "Pyhänhovi", "Pyhä HolySuites"],
  rönnäs: ["Rönnäs Country Club"],
  ruka: [
    "Rukan Lomakylä I",
    "RukaVillage Suites 1",
    "Kuusamon Pulkkajärvi 3",
    "Kuusamon Pulkkajärvi 4",
    "Kuusamon Pulkkajärvi 5",
    "Kuusamon Pulkkajärvi 6",
  ],
  saimaa: [
    "Anttilankaari 6",
    "Anttilankaari 8",
    "Anttilankaari 10",
    "Vipelentie 35",
    "Saimaanranta",
    "Saimaanranta 2",
    "Saimaanranta 3",
    "Saimaanrantapuisto",
    "Saimaan Keskuspuisto Lodge",
    "Saimaa Pearl Lodge 1",
    "Saimaa Spa Lodge 1",
    "Saimaa Spa Lodge 2",
    "Saimaa Spa Lodge 3",
  ],
  salla: [
    "Sallas huoneistot",
    "Sallan tähti",
    "Sallan Tunturitähti",
    "Sallatunturin Kelorinne",
    "Sallan Eraustähti",
    "Villas Sallatunturi 1",
    "Villas Sallatunturi 2",
  ],
  saariselkä: [
    "Kermikkä",
    "Siulaselkä",
    "Kelotirro",
    "Riekonraito",
    "Laavutieva",
    "Tirrolampi",
    "Nilihonka",
    "Saariselkä Spa Lodge 2",
    "Kelotähti 1 Lodge",
    "Ruskarinne",
  ],
  tahko: [
    "Tahkotime",
    "Leppätahko",
    "Nilsiän Rentotahko",
    "Spa Suites Black",
    "Spa Suites White",
  ],
  tampere: [
    "Näsijärven Kimallus",
    "Lapinniemi I",
    "Lapinniemi II",
    "Lapinniemi III",
    "Lapinniemi IV",
    "Lapinniemi V",
    "Lapinniemi VI",
    "Lapinniemi VII",
    "Lapinniemi VIII",
    "Lapinniemi IX",
    "Lapinniemi X",
    "Lapinniemi XI",
    "Lapinniemi XII",
    "Lapinniemi XIII",
    "Lapinniemi XIV",
    "Lapinniemi XV",
    "Lapinniemi XVI",
    "Lapinniemi XVII",
    "Lapinniemi XVIII",
    "Lapinniemi XIX",
    "Lapinniemi XX",
  ],
  turku: ["Caribia Spa Lodge 1", "HC Villas Turun Caribia"],
  vierumäki: [
    "HC Villas Vierumäki 1",
    "HC Villas Vierumäki 2",
    "Vierumäki Golf Resort",
    "Vierumäki 3",
    "Vierumäki 4",
  ],
  ylläs: [
    "Kolarin Siepakka",
    "Kesänki",
    "Kuer",
    "Muiro",
    "Musko",
    "Mokko",
    "Ylläksen Rautamajat",
    "Ylläs Saaga",
    "Ylläs viikko 1",
    "Ylläksen lomaviikot",
  ],
  ähtäri: ["Ähtärin Lomakylä", "Moksunhonka 1"],
  ulkomaat: [
    "Jardin Amadores",
    "Playa Amadores",
    "Puerto Calma",
    "Sol Amadores",
    "Vista Amadores",
    "Åre",
  ],
};
